import { client } from '@shared/openapi/ganaderia-api';
import { db, type DexieSync } from './useDexie';
import stores from '@ganaderia/stores';
import sharedStores from '@shared/shared-stores';

export default () => {
  const updateSyncLength = async () => {
    stores.offline.state.openSyncs.value = await db.syncs.count();
  };

  /** Perform a write immediately when online, otherwise queue it for later sync. */
  const queueOrSend = async (sync: Omit<DexieSync, 'timestamp'>) => {
    if (navigator.onLine) {
      return client.instance({ method: sync.method, url: sync.url, data: sync.payload });
    }
    await db.syncs.add({ ...sync, timestamp: new Date().toISOString() });
    await updateSyncLength();
    return null;
  };

  /** Replay every queued offline write against the API, remapping offline-created
   * cow ids to their real server id once the create sync for that cow succeeds. */
  const syncData = async () => {
    if (!navigator.onLine) {
      sharedStores.snackbar.funcs.notify({ text: 'No connection de internet' });
      return;
    }
    stores.app.state.loading.value = true;
    const syncs = await db.syncs.toArray();
    const idsToChange: Record<number, number> = {};
    let syncErrorLength = 0;
    for (let i = 0; i < syncs.length; i++) {
      const sync = syncs[i];
      stores.app.state.loadingMessage.value = `Syncing ${i + 1}/${syncs.length}`;
      try {
        if (sync.method === 'patch' && sync.url.startsWith('cows/')) {
          const dexieCowId = Number(sync.url.split('/').slice(-1)[0]);
          if (idsToChange[dexieCowId]) {
            sync.url = `cows/${idsToChange[dexieCowId]}`;
          }
        } else if (sync.method === 'post' && sync.url === '/cows') {
          sync.payload.id = null;
        }
        const res = await client.instance({
          method: sync.method,
          url: sync.url,
          data: sync.payload
        });
        await db.syncs.delete(sync.id!);
        if (sync.offlineAddedId) {
          idsToChange[sync.offlineAddedId] = res.data.id;
        }
      } catch (error: any) {
        syncErrorLength += 1;
        await db.syncs.update(sync.id!, {
          syncing: false,
          synced: false,
          syncError: true,
          errorMessage: error.response?.data?.detail ?? String(error)
        });
      }
    }
    await updateSyncLength();
    stores.app.state.loading.value = false;
    stores.app.state.loadingMessage.value = '';
    if (syncErrorLength > 0) {
      sharedStores.snackbar.funcs.notify({ text: `Error syncronzar datos. ${syncErrorLength} Syncs failed` });
    } else {
      sharedStores.snackbar.funcs.notify({ text: 'Datos up to date', type: 'success' });
    }
  };

  return { updateSyncLength, queueOrSend, syncData };
};
